/**
 * Integrytas Group - User-Defined Column Engine (UDC)
 * Host registry — auto-discovered, not hand-curated.
 *
 * A screen becomes a "host" (a page an admin can place custom fields on) simply by
 * calling registerUdcHost() at module load with its (page, entity). Because every
 * page component is imported eagerly (page_components.tsx), every registered screen
 * is in the registry by the time an admin signs in — no central list to maintain.
 * The set is synced to public.udc_host so the Screen Layout offers every page that
 * handles a given entity. Derived apps' screens register the same way.
 *
 * A field can only render where its entity's record is in scope, so the candidate
 * pages for a field = the registered hosts for that field's entity.
 */

import { useEffect } from 'react';
import { apiDb } from './api_client';
import type { AuthSession } from '../types/auth';

export interface UdcHost { page_code: string; entity_name: string; label: string }

// Held on globalThis so a single registry is shared even if the bundler/dev server
// loads this module as more than one instance (Vite HMR can split instances).
const STORE_KEY = '__integrytas_udc_hosts__';
function store(): Map<string, UdcHost> {
  const g = globalThis as unknown as Record<string, unknown>;
  if (!(g[STORE_KEY] instanceof Map)) g[STORE_KEY] = new Map<string, UdcHost>();
  return g[STORE_KEY] as Map<string, UdcHost>;
}

/** Declare that a screen hosts custom fields for an entity (call at module load). */
export function registerUdcHost(page_code: string, entity_name: string, label: string): void {
  if (!page_code || !entity_name) return;
  store().set(`${page_code}|${entity_name}`, { page_code, entity_name, label });
}

export function getUdcHosts(): UdcHost[] {
  return [...store().values()];
}

/** Sync the auto-discovered host set to the DB once per signed-in admin. */
export function useUdcHostSync(session: AuthSession | null): void {
  useEffect(() => {
    if (!session) return;
    // Admin gate mirrors usePageSync: the trigger-seeded SUPER_ADMIN has is_admin='Y'
    // and is granted every page with full verbs. The RPC also enforces admin
    // server-side (csm_current_is_admin), so this just avoids a wasted call.
    const isAdmin =
      session.effective_user.page_access.length > 0 &&
      session.effective_user.page_access.every(p => p.can_view && p.can_change);
    if (!isAdmin) return;
    const hosts = getUdcHosts();
    if (hosts.length === 0) return;   // never wipe the registry with an empty push
    void apiDb.rpc('udc_sync_hosts', { p_hosts: hosts });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.effective_user.user_id]);
}
